import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { smtpService } from './smtp.service';
import type { SmtpConfig } from './smtp.dto';

import { useToast } from '../../contexts/ToastContext';
const Smtp = () => {
  const { showError } = useToast();
  const navigate = useNavigate();
  const { token, logout } = useAuth();

  const [records, setRecords] = useState<SmtpConfig[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    smtpService.setLogoutCallback(logout);
  }, [logout]);

  const fetchConfigs = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const response = await smtpService.getSmtpConfigs(token, {});
      setRecords(response.data);
    } catch (err) {
      setRecords([]);
      showError(
        err instanceof Error ? err.message : 'Failed to fetch SMTP configs'
      );
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  const handleEdit = (id: number) => {
    navigate(`/smtp/edit/${id}`);
  };

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        gap={2}
      >
        <Box>
          <Typography variant="h4">SMTP Settings</Typography>
          <Typography variant="body2" color="text.secondary">
            Manage SMTP configurations used for outbound emails.
          </Typography>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : records.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            No SMTP configs found
          </Typography>
        </Paper>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Host</TableCell>
                  <TableCell>Port</TableCell>
                  <TableCell>Username</TableCell>
                  <TableCell>From Email</TableCell>
                  <TableCell>Encryption</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {records.map((item, index) => (
                  <TableRow key={item.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {index + 1}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {item.host}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{item.port}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{item.username}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{item.fromEmail}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {item.encryption?.toUpperCase() || 'NONE'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        color="primary"
                        onClick={() => handleEdit(item.id)}
                        size="small"
                      >
                        <EditIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
};

export default Smtp;
